import type { SessionEventCategory } from '@repo/types/sessions/session-types';
import type { AggregatedSessionEvent } from '@repo/utility/events';
import { getEventCategoryIcon } from '@repo/utility/events';
import type { LngLatBoundsLike, Map as MapLibreMap } from 'maplibre-gl';
import * as MapLibre from 'maplibre-gl';
import { getEventIconSvg } from '../game-map/hooks/fontUtils';
import {
    COLOR_TELEPORT_LINE,
    COLOR_TELEPORT_MARKER,
    COLOR_TRAIL_SOLID,
    EVENT_CATEGORY_COLORS,
    FLAG_SIZE_MULTIPLIER,
    FLAG_SVG,
    LINE_WIDTH_MAX,
    OPACITY_TELEPORT_MARKER,
    RADIUS_FINISH,
    RADIUS_START,
    RADIUS_START_INNER,
    RADIUS_TELEPORT_MARKER,
    TELEPORT_LINE_DASH,
} from '../game-map/renderers/trail-rendering';
import { detectTeleports, filterOriginPoints, getWalkBoundaryCoordinates } from '../game-map/utils/teleport-detection';
import {
    canvasToBlob,
    downloadBlob,
    drawCircularImage,
    drawPlaceholderCircle,
    drawRoundedImage,
    drawTruncatedText,
    generateShareFilename,
    loadImage,
} from './share-image-utils';

export interface ShareImageEventMarker {
    coordinate: [number, number];
    category: SessionEventCategory;
}

export interface ShareImageData {
    gameName: string;
    gameCapsuleUrl: string;
    username: string;
    userAvatarUrl?: string;
    sessionDate: Date;
    trailBounds: LngLatBoundsLike;
    trailCoordinates: [number, number][];
    maxZoom?: number;
    events?: ShareImageEventMarker[];
    aggregatedEvents?: AggregatedSessionEvent[];
}

export interface ShareImageTranslations {
    wasPlaying: string;
    yourFavorite: string;
    gamesAt: string;
}

// Layout
const PADDING = 32;
const TEXT_GAP = 20;

// Header
const LOGO_HEIGHT = 34;
const LOGO_WIDTH = (725 / 109) * LOGO_HEIGHT; // ~226px, maintains SVG aspect ratio
const BRANDING_LINE_HEIGHT = 24;
const BRANDING_FONT_SIZE = 20;

// Map
const MAP_WIDTH = 1152;
const MAP_HEIGHT = 1023;
const MAP_BORDER_RADIUS = 60;
const MAP_FIT_PADDING = 40;
const MAP_MAX_ZOOM_FALLBACK = 15;
const MAP_RENDER_TIMEOUT = 30000;

// Footer
const FOOTER_VERTICAL_OFFSET = 8;
const TITLE_FONT_SIZE = 48;
const TITLE_GAP = 16;
const USERNAME_TEXT_HEIGHT = 22;
const AVATAR_SIZE = 32;
const AVATAR_TEXT_GAP = 12;
const CAPSULE_BORDER_RADIUS = 12;
const CAPSULE_HEIGHT = USERNAME_TEXT_HEIGHT + TITLE_GAP + TITLE_FONT_SIZE;
const CAPSULE_WIDTH = Math.round(CAPSULE_HEIGHT * (2 / 3)); // Steam capsule 600x900 aspect ratio

// Events grid (footer-inline, right-aligned)
const EVENTS_GRID_MAX = 3;
const EVENTS_COL_GAP = 16;
const EVENTS_COL_WIDTH = 184;
const EVENTS_ICON_CIRCLE_SIZE = 40;
const EVENTS_ICON_SIZE = 20;
const EVENTS_TEXT_OFFSET_X = EVENTS_ICON_CIRCLE_SIZE + 12;
const EVENTS_NAME_FONT_SIZE = 14;
const EVENTS_COUNT_FONT_SIZE = 24;
const EVENTS_MAX_TEXT_WIDTH = EVENTS_COL_WIDTH - EVENTS_TEXT_OFFSET_X;
const EVENTS_GRID_WIDTH = EVENTS_GRID_MAX * EVENTS_COL_WIDTH + (EVENTS_GRID_MAX - 1) * EVENTS_COL_GAP;
const EVENTS_TEXT_GAP = 24;
const EVENTS_COUNT_Y_OFFSET = 26;

// Derived dimensions
const IMAGE_WIDTH = MAP_WIDTH + PADDING * 2;
const IMAGE_HEIGHT = PADDING + LOGO_HEIGHT + PADDING + MAP_HEIGHT + PADDING + CAPSULE_HEIGHT + PADDING;
const EVENTS_GRID_X = IMAGE_WIDTH - PADDING - EVENTS_GRID_WIDTH;

const WAND_LOGO_SVG = `<svg viewBox="0 0 725 109" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M683.729 -3.05176e-05H554.942V108.715H683.34C706.354 108.715 725.01 90.0591 725.01 67.0458V41.2804C725.01 18.4864 706.533 -3.05176e-05 683.729 -3.05176e-05ZM678.275 61.9804L601.498 61.9206V46.7147L678.275 46.6847V61.9804Z" fill="white"/>
    <path d="M123.522 61.6614H108.516V0H61.9805V61.6614H46.595L46.4554 0H0V69.1996C0 90.6873 17.4195 108.416 38.9173 108.416H169.978V0H123.522V61.6614Z" fill="white"/>
    <path d="M493.57 61.9406H478.285V0.199392H408.616C387.228 0.199392 369.889 17.5392 369.889 38.9272V108.715H416.304V46.6149H431.869V108.715H502.056C523.005 108.715 539.986 91.7343 539.986 70.7849V-3.05176e-05H493.57V61.9406Z" fill="white"/>
    <path d="M354.902 50.9125L354.942 108.376H308.487V46.4554H231.43V61.6614H293.39V108.416H184.915V38.4087C184.915 17.1902 202.115 -3.05176e-05 223.323 -3.05176e-05L316.194 0.109652C337.582 0.109652 354.922 17.24 354.922 38.6281L354.882 50.9125H354.902Z" fill="white"/>
</svg>`;

interface MapCaptureResult {
    canvas: HTMLCanvasElement;
    startPixel: { x: number; y: number };
    endPixel: { x: number; y: number };
    eventPixels: Array<{ x: number; y: number; category: SessionEventCategory }>;
    teleportMarkerPixels: Array<{ x: number; y: number }>;
}

/**
 * Captures the map by creating a hidden map instance at the specified dimensions.
 * This avoids aspect ratio issues that occur when cropping a different viewport.
 * Returns the canvas plus pixel coordinates of start/end points for custom marker rendering.
 */
async function captureMapCanvas(
    sourceMap: MapLibreMap,
    bounds: LngLatBoundsLike,
    trailCoordinates: [number, number][],
    width: number,
    height: number,
    maxZoom: number,
    events?: ShareImageEventMarker[],
): Promise<MapCaptureResult> {
    const filteredCoordinates = filterOriginPoints(trailCoordinates);
    if (filteredCoordinates.length < 2) {
        throw new Error('Trail must have at least 2 coordinates');
    }

    const segments = detectTeleports(filteredCoordinates);
    const walkSegments = segments.filter(s => s.type === 'walk');
    const teleportSegments = segments.filter(s => s.type === 'teleport');
    const hasTeleports = teleportSegments.length > 0;

    const { start: startCoord, end: endCoord } = getWalkBoundaryCoordinates(segments, filteredCoordinates);

    return new Promise((resolve, reject) => {
        const hidden = document.createElement('div');
        hidden.style.cssText = 'position: fixed; left: -9999px; top: -9999px; pointer-events: none;';
        document.body.appendChild(hidden);

        const container = document.createElement('div');
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        hidden.appendChild(container);

        const sourceStyle = sourceMap.getStyle();

        const includedLayers = sourceStyle.layers.filter(layer => layer.id === 'map-tiles');

        // preserveDrawingBuffer is required for canvas export in MapLibre v5+
        const renderMap = new MapLibre.Map({
            container: container,
            style: {
                version: 8,
                sources: sourceStyle.sources,
                layers: includedLayers,
                glyphs: sourceStyle.glyphs,
                ...(sourceStyle.sprite && { sprite: sourceStyle.sprite }),
            },
            center: sourceMap.getCenter(),
            zoom: sourceMap.getZoom(),
            bearing: 0,
            pitch: 0,
            interactive: false,
            canvasContextAttributes: {
                preserveDrawingBuffer: true,
            },
            fadeDuration: 0,
            attributionControl: false,
            renderWorldCopies: false,
        });

        const timeout = setTimeout(() => {
            cleanup();
            reject(new Error('Map render timeout'));
        }, MAP_RENDER_TIMEOUT);

        const cleanup = () => {
            clearTimeout(timeout);
            try {
                renderMap.remove();
            } catch {
                // Ignore cleanup errors
            }
            hidden.remove();
        };

        renderMap.once('load', () => {
            const sourceImages = sourceMap.listImages();
            for (const imageName of sourceImages) {
                if (!renderMap.hasImage(imageName)) {
                    const image = sourceMap.getImage(imageName);
                    if (image) {
                        renderMap.addImage(imageName, image.data, {
                            pixelRatio: image.pixelRatio,
                            sdf: image.sdf,
                        });
                    }
                }
            }

            const walkFeatures: GeoJSON.Feature[] = walkSegments.map(seg => ({
                type: 'Feature' as const,
                properties: {},
                geometry: {
                    type: 'LineString' as const,
                    coordinates: seg.coordinates,
                },
            }));

            renderMap.addSource('share-trail', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: walkFeatures,
                },
            });

            renderMap.addLayer({
                id: 'share-trail-line',
                type: 'line',
                source: 'share-trail',
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round',
                },
                paint: {
                    'line-color': COLOR_TRAIL_SOLID,
                    'line-width': LINE_WIDTH_MAX,
                },
            });

            if (hasTeleports) {
                const teleportLineFeatures: GeoJSON.Feature[] = [];
                for (const seg of teleportSegments) {
                    const start = seg.coordinates[0];
                    const end = seg.coordinates[seg.coordinates.length - 1];
                    if (start && end) {
                        teleportLineFeatures.push({
                            type: 'Feature',
                            properties: {},
                            geometry: { type: 'LineString', coordinates: [start, end] },
                        });
                    }
                }

                renderMap.addSource('share-teleport', {
                    type: 'geojson',
                    data: { type: 'FeatureCollection', features: teleportLineFeatures },
                });

                renderMap.addLayer(
                    {
                        id: 'share-teleport-line',
                        type: 'line',
                        source: 'share-teleport',
                        layout: {
                            'line-cap': 'round',
                            'line-join': 'round',
                        },
                        paint: {
                            'line-color': COLOR_TELEPORT_LINE,
                            'line-dasharray': TELEPORT_LINE_DASH,
                            'line-width': LINE_WIDTH_MAX,
                        },
                    },
                    'share-trail-line',
                );
            }

            // Use walk-only coordinates so loading junk at origin doesn't distort the bounds
            const filteredCoords = walkSegments.flatMap(s => s.coordinates);
            const filteredBounds =
                filteredCoords.length >= 2
                    ? filteredCoords.reduce(
                          (b, coord) => b.extend(coord as [number, number]),
                          new MapLibre.LngLatBounds(
                              filteredCoords[0] as [number, number],
                              filteredCoords[0] as [number, number],
                          ),
                      )
                    : bounds;

            renderMap.fitBounds(filteredBounds, {
                padding: MAP_FIT_PADDING,
                maxZoom: maxZoom,
                animate: false,
            });

            renderMap.once('idle', () => {
                try {
                    const canvas = renderMap.getCanvas();
                    const outputCanvas = document.createElement('canvas');
                    outputCanvas.width = width;
                    outputCanvas.height = height;
                    const ctx = outputCanvas.getContext('2d');

                    if (!ctx) {
                        cleanup();
                        reject(new Error('Failed to get canvas context'));
                        return;
                    }

                    if (!startCoord || !endCoord) {
                        cleanup();
                        reject(new Error('Could not determine trail start/end coordinates'));
                        return;
                    }

                    const startPixel = renderMap.project(startCoord);
                    const endPixel = renderMap.project(endCoord);

                    const eventPixels = (events ?? []).map(e => {
                        const px = renderMap.project(e.coordinate);
                        return { x: px.x, y: px.y, category: e.category };
                    });

                    const teleportMarkerPixels: Array<{ x: number; y: number }> = [];
                    if (hasTeleports) {
                        for (const seg of teleportSegments) {
                            const departCoord = seg.coordinates[0];
                            const arriveCoord = seg.coordinates[seg.coordinates.length - 1];
                            if (departCoord) {
                                const px = renderMap.project(departCoord);
                                teleportMarkerPixels.push({ x: px.x, y: px.y });
                            }
                            if (arriveCoord) {
                                const px = renderMap.project(arriveCoord);
                                teleportMarkerPixels.push({ x: px.x, y: px.y });
                            }
                        }
                    }

                    ctx.drawImage(canvas, 0, 0, width, height);
                    cleanup();
                    resolve({
                        canvas: outputCanvas,
                        startPixel: { x: startPixel.x, y: startPixel.y },
                        endPixel: { x: endPixel.x, y: endPixel.y },
                        eventPixels,
                        teleportMarkerPixels,
                    });
                } catch (error) {
                    cleanup();
                    reject(error);
                }
            });
        });

        renderMap.on('error', e => {
            cleanup();
            reject(e.error);
        });
    });
}

async function drawWandLogo(ctx: CanvasRenderingContext2D): Promise<number> {
    const logoY = PADDING;
    const logoBottom = logoY + LOGO_HEIGHT;

    try {
        const blob = new Blob([WAND_LOGO_SVG], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        try {
            const logo = await loadImage(url);
            ctx.drawImage(logo, PADDING, logoY, LOGO_WIDTH, LOGO_HEIGHT);
        } finally {
            URL.revokeObjectURL(url);
        }
    } catch {
        ctx.font = '500 32px Artex, sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText('WAND', PADDING, logoY + LOGO_HEIGHT / 2);
    }

    return logoBottom;
}

function drawRoundedMap(ctx: CanvasRenderingContext2D, mapCanvas: HTMLCanvasElement, x: number, y: number): void {
    ctx.save();
    ctx.beginPath();
    ctx.roundRect(x, y, MAP_WIDTH, MAP_HEIGHT, MAP_BORDER_RADIUS);
    ctx.clip();
    ctx.drawImage(mapCanvas, x, y, MAP_WIDTH, MAP_HEIGHT);
    ctx.restore();
}

function drawStartMarker(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    ctx.beginPath();
    ctx.arc(x, y, RADIUS_START, 0, Math.PI * 2);
    ctx.fillStyle = COLOR_TRAIL_SOLID;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(x, y, RADIUS_START_INNER, 0, Math.PI * 2);
    ctx.fillStyle = '#000000';
    ctx.fill();
}

async function drawFinishMarker(ctx: CanvasRenderingContext2D, x: number, y: number): Promise<void> {
    ctx.beginPath();
    ctx.arc(x, y, RADIUS_FINISH, 0, Math.PI * 2);
    ctx.fillStyle = COLOR_TRAIL_SOLID;
    ctx.fill();

    const flagSize = RADIUS_FINISH * FLAG_SIZE_MULTIPLIER;
    try {
        const blob = new Blob([FLAG_SVG], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        try {
            const flagImg = await loadImage(url);
            ctx.drawImage(flagImg, x - flagSize / 2, y - flagSize / 2, flagSize, flagSize);
        } finally {
            URL.revokeObjectURL(url);
        }
    } catch {
        ctx.fillStyle = '#000000';
        ctx.fillRect(x - 2, y - RADIUS_FINISH * 0.5, 3, RADIUS_FINISH);
    }
}

async function drawGameCapsule(ctx: CanvasRenderingContext2D, capsuleUrl: string): Promise<void> {
    const x = PADDING;
    const y = IMAGE_HEIGHT - PADDING - CAPSULE_HEIGHT;

    try {
        const capsule = await loadImage(capsuleUrl);
        drawRoundedImage(ctx, capsule, x, y, CAPSULE_WIDTH, CAPSULE_HEIGHT, CAPSULE_BORDER_RADIUS);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(x, y, CAPSULE_WIDTH, CAPSULE_HEIGHT, CAPSULE_BORDER_RADIUS);
        ctx.stroke();
    } catch {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.beginPath();
        ctx.roundRect(x, y, CAPSULE_WIDTH, CAPSULE_HEIGHT, CAPSULE_BORDER_RADIUS);
        ctx.fill();
    }
}

async function drawUserInfo(
    ctx: CanvasRenderingContext2D,
    username: string,
    gameName: string,
    wasPlayingText: string,
    footerY: number,
    avatarUrl?: string,
    hasEvents?: boolean,
): Promise<void> {
    const textXBase = PADDING + CAPSULE_WIDTH + TEXT_GAP;
    const capsuleY = footerY;
    const capsuleBottom = capsuleY + CAPSULE_HEIGHT;

    const rightEdge = hasEvents ? EVENTS_GRID_X - EVENTS_TEXT_GAP : IMAGE_WIDTH - PADDING;
    const gameNameMaxWidth = rightEdge - textXBase;

    // Title bottom aligned with capsule bottom, shifted down for visual balance
    const titleY = capsuleBottom + FOOTER_VERTICAL_OFFSET;
    // User line centered in the remaining space above the title
    const userLineCenterY = capsuleY + USERNAME_TEXT_HEIGHT / 2 + FOOTER_VERTICAL_OFFSET;

    let textX = textXBase;
    if (avatarUrl) {
        const avatarY = userLineCenterY - AVATAR_SIZE / 2;
        try {
            const avatar = await loadImage(avatarUrl);
            drawCircularImage(ctx, avatar, textXBase, avatarY, AVATAR_SIZE);
        } catch {
            drawPlaceholderCircle(ctx, textXBase, avatarY, AVATAR_SIZE);
        }
        textX = textXBase + AVATAR_SIZE + AVATAR_TEXT_GAP;
    }

    const maxTextWidth = rightEdge - textX;

    ctx.font = `500 ${USERNAME_TEXT_HEIGHT}px Artex, sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    drawTruncatedText(ctx, `${username} ${wasPlayingText}`.toUpperCase(), textX, userLineCenterY, maxTextWidth);

    ctx.font = `500 ${TITLE_FONT_SIZE}px Artex, sans-serif`;
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'bottom';
    drawTruncatedText(ctx, gameName, textXBase, titleY, gameNameMaxWidth);
}

function drawBrandingText(
    ctx: CanvasRenderingContext2D,
    logoBottom: number,
    translations: ShareImageTranslations,
): void {
    const x = IMAGE_WIDTH - PADDING;
    const bottomLineY = logoBottom;
    const topLineY = bottomLineY - BRANDING_LINE_HEIGHT;

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'alphabetic';

    ctx.font = `400 ${BRANDING_FONT_SIZE}px Artex, sans-serif`;
    ctx.fillText(translations.yourFavorite, x, topLineY);

    const wandComText = 'wand.com';

    ctx.font = `500 ${BRANDING_FONT_SIZE}px Artex, sans-serif`;
    const wandComWidth = ctx.measureText(wandComText).width;

    ctx.font = `400 ${BRANDING_FONT_SIZE}px Artex, sans-serif`;
    ctx.fillText(`${translations.gamesAt} `, x - wandComWidth, bottomLineY);
    ctx.font = `500 ${BRANDING_FONT_SIZE}px Artex, sans-serif`;
    ctx.fillText(wandComText, x, bottomLineY);
}

function drawTeleportMarkers(ctx: CanvasRenderingContext2D, markers: Array<{ x: number; y: number }>): void {
    for (const marker of markers) {
        ctx.beginPath();
        ctx.arc(marker.x, marker.y, RADIUS_TELEPORT_MARKER, 0, Math.PI * 2);
        ctx.fillStyle = COLOR_TELEPORT_MARKER;
        ctx.globalAlpha = OPACITY_TELEPORT_MARKER;
        ctx.fill();
        ctx.globalAlpha = 1;
    }
}

const EVENT_MARKER_RADIUS = 12;
const EVENT_MARKER_STROKE = 2;
const EVENT_ICON_SIZE = 12;

async function drawEventMarkers(
    ctx: CanvasRenderingContext2D,
    eventPixels: Array<{ x: number; y: number; category: SessionEventCategory }>,
): Promise<void> {
    const categories = [...new Set(eventPixels.map(e => e.category))];
    const iconCache = new Map<string, HTMLImageElement>();

    await Promise.all(
        categories.map(async category => {
            const iconName = getEventCategoryIcon(category);
            try {
                const svg = await getEventIconSvg(iconName, EVENT_ICON_SIZE);
                if (!svg) return;
                const img = await loadImage(`data:image/svg+xml;base64,${btoa(svg)}`);
                iconCache.set(category, img);
            } catch {
                // ignore — draw circle without icon
            }
        }),
    );

    for (const event of eventPixels) {
        const color = EVENT_CATEGORY_COLORS[event.category] || EVENT_CATEGORY_COLORS.general;

        ctx.beginPath();
        ctx.arc(event.x, event.y, EVENT_MARKER_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = EVENT_MARKER_STROKE;
        ctx.stroke();

        const icon = iconCache.get(event.category);
        if (icon) {
            ctx.drawImage(
                icon,
                event.x - EVENT_ICON_SIZE / 2,
                event.y - EVENT_ICON_SIZE / 2,
                EVENT_ICON_SIZE,
                EVENT_ICON_SIZE,
            );
        }
    }
}

async function drawEventsGrid(
    ctx: CanvasRenderingContext2D,
    events: AggregatedSessionEvent[],
    footerY: number,
): Promise<void> {
    const displayEvents = events.slice(0, EVENTS_GRID_MAX);
    const startX = EVENTS_GRID_X + (EVENTS_GRID_MAX - displayEvents.length) * (EVENTS_COL_WIDTH + EVENTS_COL_GAP);
    const startY = footerY + FOOTER_VERTICAL_OFFSET;

    // Compute event name Y so its cap-top aligns with the username's cap-top.
    // Username is drawn with textBaseline='middle' at usernameMiddleY.
    const usernameMiddleY = startY + USERNAME_TEXT_HEIGHT / 2;
    ctx.font = `500 ${USERNAME_TEXT_HEIGHT}px Artex, sans-serif`;
    ctx.textBaseline = 'middle';
    const userMetrics = ctx.measureText('A');
    const capTop = usernameMiddleY - userMetrics.actualBoundingBoxAscent;

    ctx.font = `500 ${EVENTS_NAME_FONT_SIZE}px Artex, sans-serif`;
    ctx.textBaseline = 'alphabetic';
    const eventMetrics = ctx.measureText('A');
    const nameBaselineY = capTop + eventMetrics.actualBoundingBoxAscent;

    // Pre-load all unique icons
    const uniqueIcons = [...new Set(displayEvents.map(e => e.icon))];
    const iconCache = new Map<string, HTMLImageElement>();

    await Promise.all(
        uniqueIcons.map(async iconName => {
            try {
                const svg = await getEventIconSvg(iconName, EVENTS_ICON_SIZE);
                if (!svg) return;
                const img = await loadImage(`data:image/svg+xml;base64,${btoa(svg)}`);
                iconCache.set(iconName, img);
            } catch {
                // ignore — draw circle without icon
            }
        }),
    );

    for (const [i, event] of displayEvents.entries()) {
        const x = startX + i * (EVENTS_COL_WIDTH + EVENTS_COL_GAP);
        const y = startY;
        const circleRadius = EVENTS_ICON_CIRCLE_SIZE / 2;

        // Icon circle background
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.beginPath();
        ctx.arc(x + circleRadius, y + circleRadius, circleRadius, 0, Math.PI * 2);
        ctx.fill();

        // Icon
        const icon = iconCache.get(event.icon);
        if (icon) {
            ctx.drawImage(
                icon,
                x + circleRadius - EVENTS_ICON_SIZE / 2,
                y + circleRadius - EVENTS_ICON_SIZE / 2,
                EVENTS_ICON_SIZE,
                EVENTS_ICON_SIZE,
            );
        }

        // Event name — alphabetic baseline aligned with "username was playing"
        ctx.font = `500 ${EVENTS_NAME_FONT_SIZE}px Artex, sans-serif`;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        drawTruncatedText(
            ctx,
            event.displayName.toUpperCase(),
            x + EVENTS_TEXT_OFFSET_X,
            nameBaselineY,
            EVENTS_MAX_TEXT_WIDTH,
        );

        // Event count
        ctx.font = `500 ${EVENTS_COUNT_FONT_SIZE}px Artex, sans-serif`;
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(String(event.count), x + EVENTS_TEXT_OFFSET_X, nameBaselineY + EVENTS_COUNT_Y_OFFSET);
    }
}

export async function generateShareImage(
    map: MapLibreMap,
    data: ShareImageData,
    translations: ShareImageTranslations,
): Promise<Blob> {
    const maxZoom = data.maxZoom ?? MAP_MAX_ZOOM_FALLBACK;
    const mapResult = await captureMapCanvas(
        map,
        data.trailBounds,
        data.trailCoordinates,
        MAP_WIDTH,
        MAP_HEIGHT,
        maxZoom,
        data.events,
    );

    const mapCtx = mapResult.canvas.getContext('2d');
    if (!mapCtx) {
        console.warn('Failed to get 2D context for map canvas; start/end markers will not be drawn.');
    } else {
        if (mapResult.teleportMarkerPixels.length > 0) {
            drawTeleportMarkers(mapCtx, mapResult.teleportMarkerPixels);
        }
        drawStartMarker(mapCtx, mapResult.startPixel.x, mapResult.startPixel.y);
        await drawFinishMarker(mapCtx, mapResult.endPixel.x, mapResult.endPixel.y);
        if (mapResult.eventPixels.length > 0) {
            await drawEventMarkers(mapCtx, mapResult.eventPixels);
        }
    }

    const aggregatedEvents = data.aggregatedEvents ?? [];
    const hasEvents = aggregatedEvents.length > 0;

    const canvas = document.createElement('canvas');
    canvas.width = IMAGE_WIDTH;
    canvas.height = IMAGE_HEIGHT;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Failed to get canvas context');
    }

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, IMAGE_WIDTH, IMAGE_HEIGHT);

    const mapX = PADDING;
    const mapY = PADDING + LOGO_HEIGHT + PADDING;
    drawRoundedMap(ctx, mapResult.canvas, mapX, mapY);

    const logoBottom = await drawWandLogo(ctx);
    drawBrandingText(ctx, logoBottom, translations);

    const footerY = IMAGE_HEIGHT - PADDING - CAPSULE_HEIGHT;
    await drawGameCapsule(ctx, data.gameCapsuleUrl);
    await drawUserInfo(
        ctx,
        data.username,
        data.gameName,
        translations.wasPlaying,
        footerY,
        data.userAvatarUrl,
        hasEvents,
    );

    if (hasEvents) {
        await drawEventsGrid(ctx, aggregatedEvents, footerY);
    }

    return canvasToBlob(canvas);
}

export async function generateAndDownloadShareImage(
    map: MapLibreMap,
    data: ShareImageData,
    translations: ShareImageTranslations,
): Promise<void> {
    const blob = await generateShareImage(map, data, translations);
    downloadBlob(blob, generateShareFilename(data.gameName, data.sessionDate));
}
